import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { profile } from '../mock';
import { toast } from 'sonner';

function FormField({ id, label, type = 'text', value, onChange, placeholder, multiline = false, rows = 5 }) {
  const baseClass =
    'mt-2 w-full bg-transparent border-b border-cream/30 py-2 text-cream focus:outline-none focus:border-orange-acc transition-colors';
  return (
    <div>
      <label htmlFor={id} className="text-[10px] uppercase tracking-[0.3em] text-orange-acc">
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          rows={rows}
          value={value}
          onChange={onChange}
          className={`${baseClass} resize-none`}
          placeholder={placeholder}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          className={baseClass}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function ContactDetails() {
  return (
    <ul className="mt-10 space-y-5 text-cream">
      <li className="flex items-center gap-4">
        <span className="w-10 h-10 rounded-full border border-cream/30 grid place-items-center text-orange-acc">
          <Mail size={16} />
        </span>
        <a href={`mailto:${profile.email}`} className="hover:text-orange-acc">
          {profile.email}
        </a>
      </li>
      <li className="flex items-center gap-4">
        <span className="w-10 h-10 rounded-full border border-cream/30 grid place-items-center text-orange-acc">
          <Phone size={16} />
        </span>
        <a href={`tel:${profile.phone}`} className="hover:text-orange-acc">
          {profile.phone}
        </a>
      </li>
      <li className="flex items-center gap-4">
        <span className="w-10 h-10 rounded-full border border-cream/30 grid place-items-center text-orange-acc">
          <MapPin size={16} />
        </span>
        <span>{profile.location}</span>
      </li>
    </ul>
  );
}

function SocialBadges() {
  return (
    <div className="mt-10 flex flex-wrap gap-3">
      {profile.socials.map((s) => (
        <a
          key={s.label}
          href={s.url}
          className="px-4 py-2 border border-cream/30 text-xs uppercase tracking-[0.22em] text-cream hover:border-orange-acc hover:text-orange-acc transition-colors"
        >
          {s.label}
        </a>
      ))}
    </div>
  );
}

function validateForm({ name, email, message }) {
  if (!name.trim() || !email.trim() || !message.trim()) {
    return 'Please fill in all the fields.';
  }
  return null;
}

function persistMessageLocally(payload) {
  const stored = JSON.parse(localStorage.getItem('sc_messages') || '[]');
  stored.push({ ...payload, at: new Date().toISOString() });
  localStorage.setItem('sc_messages', JSON.stringify(stored));
}

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSending(true);
    setTimeout(() => {
      persistMessageLocally(form);
      setForm({ name: '', email: '', message: '' });
      setSending(false);
      toast.success('Thanks — your note is on its way to Shreya.');
    }, 700);
  };

  return (
    <form onSubmit={onSubmit} className="bg-navy-2 border border-cream p-7 md:p-10 space-y-6">
      <FormField
        id="name"
        label="Your name"
        value={form.name}
        onChange={update('name')}
        placeholder="Jane Doe"
      />
      <FormField
        id="email"
        label="Email"
        type="email"
        value={form.email}
        onChange={update('email')}
        placeholder="hello@studio.com"
      />
      <FormField
        id="message"
        label="Message"
        multiline
        value={form.message}
        onChange={update('message')}
        placeholder="Tell me about your idea…"
      />
      <button
        type="submit"
        disabled={sending}
        className="btn-orange w-full md:w-auto px-8 py-3.5 text-xs uppercase tracking-[0.25em] rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {sending ? 'Sending…' : <>Send note <Send size={14} /></>}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-cream">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-12">
          <span className="font-display text-orange-acc text-2xl">04</span>
          <span className="text-[11px] uppercase tracking-[0.32em] text-cream-soft/70">Contact</span>
          <div className="flex-1 h-px bg-cream/20" />
        </div>

        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <h3 className="font-display text-5xl md:text-7xl leading-[0.95] text-cream">
              Let&rsquo;s make something <span className="italic font-serif-display text-orange-acc">together</span>.
            </h3>
            <p className="mt-6 text-cream-soft/80 max-w-md">
              Open to internships, collaborations, illustration commissions and the occasional curious conversation.
            </p>
            <ContactDetails />
            <SocialBadges />
          </div>

          <div className="md:col-span-6">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}
